import { Box, chakra, Flex, Tag, TagCloseButton, TagLabel } from '@chakra-ui/react';
import { AvatarGroup } from '@main/complykit/components/avatar';
import { Text } from '@main/complykit/components/text';
import { Tooltip } from '@main/complykit/components/tooltip';
import { MultiValue } from 'chakra-react-select';
import { Children, ReactNode } from 'react';
import { Focusable } from 'react-aria-components';

import { Combobox, ComboboxProps, selectValueToArray, withMultiCombobox } from '../combobox';
import {
  AvatarOption,
  AvatarOptionIcon,
  AvatarOptionProps,
  AvatarOptionVariant,
} from './avatar-option';

export const defaultProps = {
  getOptionValue: (option) => option.id,
  getOptionLabel: (option) => option.displayName,

  components: {
    Option: ({ displayName, avatarUrl, kind, size = 'xs' }) => {
      return (
        <Flex gap={2} alignItems="center">
          <AvatarOptionIcon
            size={size}
            displayName={displayName}
            avatarUrl={avatarUrl}
            kind={kind}
          />
          <Text size="xs" weight="medium" color="muted">
            {displayName}
          </Text>
        </Flex>
      );
    },
    SelectedOption: ({ displayName, avatarUrl, kind, size = 'xs', removeProps, menuIsOpen }) => {
      return (
        <Tag bg="none" minW="auto" maxW="none" px={0}>
          <AvatarOptionIcon
            size={size}
            displayName={displayName}
            avatarUrl={avatarUrl}
            kind={kind}
          />
          {menuIsOpen && (
            <TagLabel ml={2} fontSize="xs" color="gray.500">
              {displayName}
            </TagLabel>
          )}
          {removeProps && (
            <Box as={TagCloseButton} {...removeProps} aria-label={`Remove ${displayName}`} />
          )}
        </Tag>
      );
    },

    OptionContainer: ({ children, menuIsOpen, values, groupSize }) => {
      if (menuIsOpen) {
        return children;
      }

      const childrenArray = Children.toArray(children);
      const inputElement = childrenArray.pop();
      return (
        <Flex w="full">
          <GroupedAvatars groupSize={groupSize} values={selectValueToArray(values)}>
            {childrenArray}
          </GroupedAvatars>
          {inputElement}
        </Flex>
      );
    },
  },
} satisfies ComboboxProps<AvatarOptionProps>;

export const GroupedAvatars = ({
  children,
  values,
  groupSize = 4,
  size = 'xs',
  variant = 'detailed',
  hasTooltip = true,
}: {
  children: ReactNode;
  values: MultiValue<AvatarOptionProps>;
  groupSize?: number;
  size?: 'xs' | 'sm';
  variant?: AvatarOptionVariant;
  /** Off where the names are already listed, or inside another button. */
  hasTooltip?: boolean;
}) => {
  const avatars = Children.toArray(children);
  const label = values.map((value) => value.displayName).join(', ');
  const showsTooltip =
    hasTooltip && values.length > 0 && !(values.length === 1 && variant === 'detailed');

  const group = (
    <Flex
      role={showsTooltip ? 'button' : undefined}
      data-testid="grouped-avatars-trigger"
      display="inline-flex"
      w="fit-content"
    >
      {/* One value in the detailed variant shows the person and their name. */}
      {variant === 'detailed' && avatars.length === 1 ? (
        <Flex alignItems="center" gap={2}>
          {avatars[0]}
          <Text size="xs" weight="medium" color="muted">
            {values[0]?.displayName}
          </Text>
        </Flex>
      ) : (
        <AvatarGroup label={label} max={groupSize} size={size === 'xs' ? 'sm' : 'md'}>
          {avatars}
        </AvatarGroup>
      )}
    </Flex>
  );

  if (!showsTooltip) return group;

  return (
    <Tooltip content={label} placement="top">
      {/* Focusable wraps the Chakra trigger itself (tooltip.md), so the
       * names are one Tab away as well as one hover away; the role is the
       * lib's convention for a plain tooltip trigger. */}
      <Focusable>{group}</Focusable>
    </Tooltip>
  );
};

export function EditableMultiSelectAvatar<TOption extends AvatarOption>(
  props: Omit<ComboboxProps<TOption, true>, 'components' | 'isMulti'>,
) {
  return <Combobox {...defaultProps} {...withMultiCombobox({ ...props, isMulti: true })} />;
}

EditableMultiSelectAvatar.Option = defaultProps.components.SelectedOption;
